import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Stack,
  Button,
} from "@mui/material";
import toastr from "toastr";
import {
  ExternalInvoiceForm,
  ExternalCalculations,
  ExternalInvoiceFields,
  calculateExternal,
  createExternalForm,
  generateExternalInvoicePdf,
  validateExternalForm,
} from "./GenerateInvoices/externalInvoice";
import {
  InternalIgstForm,
  InternalIgstFields,
  calculateInternalIgst,
  createInternalIgstForm,
  generateInternalIgstInvoicePdf,
  validateInternalIgstForm,
} from "./GenerateInvoices/internalIgstInvoice";
import {
  InternalSgstCgstForm,
  InternalSgstCgstFields,
  calculateInternalSgstCgst,
  createInternalSgstCgstForm,
  generateInternalSgstCgstInvoicePdf,
  validateInternalSgstCgstForm,
} from "./GenerateInvoices/internalSgstCgstInvoice";
import { InternalCalculations } from "./GenerateInvoices/internalCommon";
import { buildInvoiceNumber, ensureUuid } from "./GenerateInvoices/common";
import {
  ReferEarnInvoiceFields,
  ReferEarnInvoiceForm,
  ReferEarnState,
  calculateReferEarn,
  createReferEarnInvoiceForm,
  createReferEarnState,
  generateReferEarnInvoicePdf,
  validateReferEarnForm,
} from "./GenerateInvoices/referEarnInvoice";
import { makePOSTRequestOnUserService } from "../../Api/api";

type InvoiceType =
  | "EXTERNAL"
  | "INTERNAL_IGST"
  | "INTERNAL_SGST_CGST"
  | "REFER_EARN";

const GenerateInvoices = () => {
  const [selectedType, setSelectedType] = useState<InvoiceType>("EXTERNAL");
  const [externalForm, setExternalForm] = useState<ExternalInvoiceForm>(() =>
    createExternalForm()
  );
  const [internalIgstForm, setInternalIgstForm] = useState<InternalIgstForm>(
    () => createInternalIgstForm()
  );
  const [internalSgstCgstForm, setInternalSgstCgstForm] =
    useState<InternalSgstCgstForm>(() => createInternalSgstCgstForm());
  const [referEarnForm, setReferEarnForm] = useState<ReferEarnInvoiceForm>(() =>
    createReferEarnInvoiceForm()
  );
  const [referEarnState, setReferEarnState] = useState<ReferEarnState>(() =>
    createReferEarnState()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);

  const externalCalculations = useMemo<ExternalCalculations>(
    () => calculateExternal(externalForm),
    [externalForm]
  );

  const internalIgstCalculations = useMemo<InternalCalculations>(
    () => calculateInternalIgst(internalIgstForm),
    [internalIgstForm]
  );

  const internalSgstCalculations = useMemo<InternalCalculations>(
    () => calculateInternalSgstCgst(internalSgstCgstForm),
    [internalSgstCgstForm]
  );

  const referEarnCalculations = useMemo(
    () => calculateReferEarn(referEarnForm),
    [referEarnForm]
  );

  useEffect(() => {
    setErrors({});
  }, [selectedType]);

  const handleInvoiceTypeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedType(event.target.value as InvoiceType);
    },
    []
  );

  const handleExternalChange = useCallback(
    (field: keyof ExternalInvoiceForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setExternalForm((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const handleInternalIgstChange = useCallback(
    (field: keyof InternalIgstForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setInternalIgstForm((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const handleInternalSgstCgstChange = useCallback(
    (field: keyof InternalSgstCgstForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setInternalSgstCgstForm((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const handleReferEarnChange = useCallback(
    (field: keyof ReferEarnInvoiceForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setReferEarnForm((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const patchReferEarnForm = useCallback(
    (updates: Partial<ReferEarnInvoiceForm>) => {
      setReferEarnForm((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const handleRegenerateUuid = useCallback((context: InvoiceType) => {
    const uuid = ensureUuid();
    switch (context) {
      case "EXTERNAL":
        setExternalForm((prev) => ({ ...prev, documentUuid: uuid }));
        break;
      case "INTERNAL_IGST":
        setInternalIgstForm((prev) => ({ ...prev, documentUuid: uuid }));
        break;
      case "INTERNAL_SGST_CGST":
        setInternalSgstCgstForm((prev) => ({ ...prev, documentUuid: uuid }));
        break;
      case "REFER_EARN":
        setReferEarnForm((prev) => ({ ...prev, documentUuid: uuid }));
        break;
      default:
        break;
    }
  }, []);

  const handleApplyDefaultInvoiceNumber = useCallback(
    (context: InvoiceType) => {
      const nextNumber = buildInvoiceNumber();
      switch (context) {
        case "EXTERNAL":
          setExternalForm((prev) => ({ ...prev, invoiceNumber: nextNumber }));
          break;
        case "INTERNAL_IGST":
          setInternalIgstForm((prev) => ({
            ...prev,
            invoiceNumber: nextNumber,
          }));
          break;
        case "INTERNAL_SGST_CGST":
          setInternalSgstCgstForm((prev) => ({
            ...prev,
            invoiceNumber: nextNumber,
          }));
          break;
        case "REFER_EARN":
          setReferEarnForm((prev) => ({
            ...prev,
            invoiceNumber: nextNumber,
          }));
          break;
        default:
          break;
      }
    },
    []
  );

  const resetCurrentForm = useCallback(() => {
    if (selectedType === "EXTERNAL") {
      setExternalForm(createExternalForm());
    } else if (selectedType === "INTERNAL_IGST") {
      setInternalIgstForm(createInternalIgstForm());
    } else if (selectedType === "REFER_EARN") {
      setReferEarnForm(createReferEarnInvoiceForm());
      setReferEarnState(createReferEarnState());
    } else {
      setInternalSgstCgstForm(createInternalSgstCgstForm());
    }
    setErrors({});
  }, [selectedType]);

  const handleGenerate = useCallback(async () => {
    if (selectedType === "EXTERNAL") {
      const validation = validateExternalForm(externalForm);
      setErrors(validation);
      if (Object.keys(validation).length) {
        toastr.error("Please resolve the highlighted fields.");
        return;
      }
      setGenerating(true);
      try {
        generateExternalInvoicePdf(externalForm, externalCalculations);
        toastr.success("External invoice generated successfully.");
      } catch (error) {
        console.error(error);
        toastr.error("Failed to generate the invoice PDF.");
      } finally {
        setGenerating(false);
      }
      return;
    }

    if (selectedType === "REFER_EARN") {
      const validation = validateReferEarnForm(referEarnForm, referEarnState);
      setErrors(validation);
      if (Object.keys(validation).length) {
        toastr.error("Please resolve the highlighted fields.");
        return;
      }
      setGenerating(true);
      try {
        const pdfBlob = generateReferEarnInvoicePdf(
          referEarnForm,
          referEarnCalculations,
          referEarnState
        );

        if (!(pdfBlob instanceof Blob)) {
          throw new Error("Generated file is not a valid Blob");
        }

        const formData = new FormData();
        formData.append("file", pdfBlob, "invoice.pdf");
        const response = await makePOSTRequestOnUserService(
          "/user/upload_invoice/",
          formData
        );

        if (response.status !== 200 && response.status !== 201) {
          throw new Error("Upload failed");
        }

        toastr.success("Refer & Earn credit note generated successfully.");
      } catch (error) {
        console.error(error);
        toastr.error("Failed to generate the invoice PDF.");
      } finally {
        setGenerating(false);
      }
      return;
    }

    if (selectedType === "INTERNAL_IGST") {
      const validation = validateInternalIgstForm(internalIgstForm);
      setErrors(validation);
      if (Object.keys(validation).length) {
        toastr.error("Please resolve the highlighted fields.");
        return;
      }
      setGenerating(true);
      try {
        generateInternalIgstInvoicePdf(
          internalIgstForm,
          internalIgstCalculations
        );
        toastr.success("Internal IGST invoice generated successfully.");
      } catch (error) {
        console.error(error);
        toastr.error("Failed to generate the invoice PDF.");
      } finally {
        setGenerating(false);
      }
      return;
    }

    const validation = validateInternalSgstCgstForm(internalSgstCgstForm);
    setErrors(validation);
    if (Object.keys(validation).length) {
      toastr.error("Please resolve the highlighted fields.");
      return;
    }
    setGenerating(true);
    try {
      generateInternalSgstCgstInvoicePdf(
        internalSgstCgstForm,
        internalSgstCalculations
      );
      toastr.success("Internal SGST/CGST invoice generated successfully.");
    } catch (error) {
      console.error(error);
      toastr.error("Failed to generate the invoice PDF.");
    } finally {
      setGenerating(false);
    }
  }, [
    externalForm,
    externalCalculations,
    internalIgstForm,
    internalIgstCalculations,
    internalSgstCgstForm,
    internalSgstCalculations,
    referEarnForm,
    referEarnCalculations,
    referEarnState,
    selectedType,
  ]);

  const handleSendReferEarnEmail = async () => {
    const userId = referEarnState?.selectedUser?.id;
    if (!userId) {
      toastr.error("Please select a user before sending email.");
      return;
    }

    try {
      setGenerating(true);
      const response = await makePOSTRequestOnUserService(
        "/user/send_invoice_email/",
        {
          user_id: userId,
        }
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Email sending failed");
      }
      toastr.success("Refer & Earn invoice email sent successfully.");
    } catch (error) {
      console.error(error);
      toastr.error("Failed to send email.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
        <Typography variant="h5" gutterBottom>
          Generate Invoice PDFs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose the invoice format, fill in the details, and generate a Invoice
          PDF.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <RadioGroup
          row
          value={selectedType}
          onChange={handleInvoiceTypeChange}
          name="invoice-format"
        >
          <FormControlLabel
            value="EXTERNAL"
            control={<Radio />}
            label="External Invoice"
          />
          <FormControlLabel
            value="INTERNAL_IGST"
            control={<Radio />}
            label="Internal Invoice (IGST)"
          />
          <FormControlLabel
            value="INTERNAL_SGST_CGST"
            control={<Radio />}
            label="Internal Invoice (SGST + CGST)"
          />
          <FormControlLabel
            value="REFER_EARN"
            control={<Radio />}
            label="Refer & Earn Credit Note"
          />
        </RadioGroup>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mt: 2 }}>
          {selectedType === "EXTERNAL" && (
            <ExternalInvoiceFields
              form={externalForm}
              errors={errors}
              calculations={externalCalculations}
              onChange={handleExternalChange}
              onRegenerateUuid={() => handleRegenerateUuid("EXTERNAL")}
              onApplyDefaultInvoiceNumber={() =>
                handleApplyDefaultInvoiceNumber("EXTERNAL")
              }
            />
          )}
          {selectedType === "INTERNAL_IGST" && (
            <InternalIgstFields
              form={internalIgstForm}
              errors={errors}
              calculations={internalIgstCalculations}
              onChange={handleInternalIgstChange}
              onRegenerateUuid={() => handleRegenerateUuid("INTERNAL_IGST")}
              onApplyDefaultInvoiceNumber={() =>
                handleApplyDefaultInvoiceNumber("INTERNAL_IGST")
              }
            />
          )}
          {selectedType === "INTERNAL_SGST_CGST" && (
            <InternalSgstCgstFields
              form={internalSgstCgstForm}
              errors={errors}
              calculations={internalSgstCalculations}
              onChange={handleInternalSgstCgstChange}
              onRegenerateUuid={() =>
                handleRegenerateUuid("INTERNAL_SGST_CGST")
              }
              onApplyDefaultInvoiceNumber={() =>
                handleApplyDefaultInvoiceNumber("INTERNAL_SGST_CGST")
              }
            />
          )}
          {selectedType === "REFER_EARN" && (
            <ReferEarnInvoiceFields
              form={referEarnForm}
              errors={errors}
              calculations={referEarnCalculations}
              onChange={handleReferEarnChange}
              onFormPatch={patchReferEarnForm}
              onRegenerateUuid={() => handleRegenerateUuid("REFER_EARN")}
              onApplyDefaultInvoiceNumber={() =>
                handleApplyDefaultInvoiceNumber("REFER_EARN")
              }
              state={referEarnState}
              setState={setReferEarnState}
            />
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "Generating..." : "Generate PDF"}
          </Button>

          {selectedType === "REFER_EARN" && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSendReferEarnEmail}
              disabled={generating || !referEarnState?.selectedUser?.id}
            >
              Send E-Mail
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={resetCurrentForm}
            disabled={generating}
          >
            Reset Form
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default GenerateInvoices;
